import React from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SubHeader from '../../components/Navbars/SubHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const getThemeTokens = (isLightTheme) => ({
  cardBg: isLightTheme ? '#ffffff' : '#27293d',
  cardBorder: isLightTheme ? '#eee' : '#3c3f58',
  headerBg: isLightTheme ? '#fff' : '#27293d',
  headerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  titleColor: isLightTheme ? '#333' : '#f1f5f9',
  loadingOverlay: isLightTheme ? 'rgba(255,255,255,0.7)' : 'rgba(30, 30, 47, 0.55)',
  emptyOverlayBg: isLightTheme ? '#ffffff' : '#27293d',
  emptyOverlayBorder: isLightTheme ? '#eee' : '#3c3f58',
  emptyOverlayShadow: isLightTheme ? '0 10px 25px rgba(0,0,0,0.1)' : '0 10px 25px rgba(0,0,0,0.35)',
  emptyTextColor: isLightTheme ? '#6c757d' : '#94a3b8',
  emptyIconColor: isLightTheme ? '#6c757d' : '#94a3b8',
  emptyIconRingBorder: isLightTheme ? '#eee' : '#3c3f58',
  gridColor: isLightTheme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)',
  tickColor: isLightTheme ? '#666' : '#94a3b8',
  axisTitleColor: isLightTheme ? '#666' : '#94a3b8',
  legendColor: isLightTheme ? '#666' : '#94a3b8',
  tooltipBg: isLightTheme ? '#1f2937' : '#0f172a',
  tooltipTitle: isLightTheme ? '#f8fafc' : '#f1f5f9',
  tooltipBody: isLightTheme ? '#e2e8f0' : '#cbd5e1',
  tooltipBorder: isLightTheme ? '#334155' : '#1e293b',
  shadow: isLightTheme ? '0 4px 12px rgba(0,0,0,0.03)' : '0 2px 8px rgba(0,0,0,0.25)',
});

const CHART_PALETTES = {
  count: {
    light: '#a8cbac',
    dark: '#8fbf94',
  },
  sales: {
    light: '#66bb6a',
    dark: '#4ade80',
  },
};

const SalesPersonDashboard = ({
  statistics,
  activeColor,
  isLoading,
  shellThemeVariant = "1",
  isLightTheme = false,
}) => {
  const theme = React.useMemo(() => getThemeTokens(isLightTheme), [isLightTheme]);
  const paletteKey = isLightTheme ? 'light' : 'dark';

  const formatValue = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  };

  const getPeriodData = (periodId) => {
    const rawStats = statistics?.sales_person_stats || [];

    const periodMap = {
      'today': 'today',
      'week': 'week',
      'month': 'this_month',
      'quarter': 'this_quarter',
      'year': 'this_year'
    };

    const backendKey = periodMap[periodId] || periodId;

    const labels = rawStats.map(s => s.name);
    const counts = rawStats.map(s => s[backendKey]?.count || 0);
    const sales = rawStats.map(s => s[backendKey]?.rev || 0);

    return {
      labels,
      counts,
      sales,
      totalCount: counts.reduce((a, b) => a + b, 0),
      totalSales: sales.reduce((a, b) => a + b, 0),
    };
  };

  const periods = [
    { id: 'today', title: 'Sales Today' },
    { id: 'week', title: 'Sales This Week' },
    { id: 'month', title: 'Sales This Month' },
    { id: 'quarter', title: 'Sales This Quarter' },
    { id: 'year', title: 'Sales This Year' },
  ];

  const getChartOptions = React.useCallback(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 10 },
          color: theme.tickColor,
        }
      },
      yCounts: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { drawOnChartArea: true, color: theme.gridColor },
        grace: '10%',
        ticks: {
          font: { size: 10 },
          precision: 0,
          stepSize: 1,
          color: theme.tickColor,
        },
        title: {
          display: true,
          text: 'Count',
          font: { size: 10 },
          color: theme.axisTitleColor,
        }
      },
      ySales: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        grace: '10%',
        ticks: {
          font: { size: 10 },
          color: theme.tickColor,
          callback: (val) => '$' + Math.round(val).toLocaleString()
        },
        title: {
          display: true,
          text: 'Sales ($)',
          font: { size: 10 },
          color: theme.axisTitleColor,
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          font: { size: 10 },
          color: theme.legendColor,
        }
      },
      tooltip: {
        backgroundColor: theme.tooltipBg,
        titleColor: theme.tooltipTitle,
        bodyColor: theme.tooltipBody,
        borderColor: theme.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            if (label === 'Total Sales') return `${label}: ${formatValue(Math.round(value))}`;
            return `${label}: ${Math.round(value)}`;
          }
        }
      }
    }
  }), [theme]);

  const renderSalesPersonCard = (title, data) => {
    const hasData = data.labels.length > 0 && data.totalCount > 0;
    const countColor = CHART_PALETTES.count[paletteKey];
    const salesColor = CHART_PALETTES.sales[paletteKey];

    const chartData = {
      labels: hasData ? data.labels : ["No Data"],
      datasets: [
        {
          label: 'Count',
          data: hasData ? data.counts : [0],
          backgroundColor: countColor,
          borderColor: countColor,
          borderWidth: 1,
          yAxisID: 'yCounts',
        },
        {
          label: 'Total Sales',
          data: hasData ? data.sales : [0],
          backgroundColor: salesColor,
          borderColor: salesColor,
          borderWidth: 1,
          yAxisID: 'ySales',
        },
      ],
    };

    return (
      <Col xl="3" lg="4" md="6" className="mb-2">
        <Card className="avg-on-lot-card">
          <div className="avg-on-lot-header">
            <h4 className="avg-on-lot-title">{title}</h4>
          </div>
          <CardBody className="position-relative pt-0">
            {isLoading && (
              <div className="loading-overlay">
                <div className="pulse-loader">Loading...</div>
              </div>
            )}
            {!hasData && !isLoading && (
              <div className="no-data-overlay d-flex flex-column align-items-center justify-content-center">
                <div className="no-data-icon-ring mb-2">
                  <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '32px', color: theme.emptyIconColor }} />
                </div>
                <p className="small mb-3 italic" style={{ color: theme.emptyTextColor }}>No Data Qualified</p>
                <button className="btn btn-primary btn-sm px-4">Dismiss</button>
              </div>
            )}
            <div className="chart-wrapper" style={{ height: '320px', opacity: hasData ? 1 : 0.1 }}>
              <Bar data={chartData} options={getChartOptions()} />
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <div className="sales-person-dashboard-container">
      <SubHeader
        activeColor={activeColor}
        back={"/dashboard"}
        title={"Sales Person Dashboard"}
        theme2ScrollGapFill={shellThemeVariant === "2"}
        isLightTheme={isLightTheme}
      />

      <style>{`
        .sales-person-dashboard-container .avg-on-lot-card {
          background: ${theme.cardBg};
          border: 1px solid ${theme.cardBorder};
          box-shadow: ${theme.shadow};
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .sales-person-dashboard-container .avg-on-lot-header {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px;
          border-bottom: 1px solid ${theme.headerBorder};
          background: ${theme.headerBg};
        }
        .sales-person-dashboard-container .avg-on-lot-title {
          font-weight: 600;
          font-size: 13px;
          color: ${theme.titleColor};
          margin: 0;
        }
        .sales-person-dashboard-container .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${theme.loadingOverlay};
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sales-person-dashboard-container .no-data-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80%;
          background: ${theme.emptyOverlayBg};
          padding: 20px;
          border: 1px solid ${theme.emptyOverlayBorder};
          box-shadow: ${theme.emptyOverlayShadow};
          z-index: 5;
          text-align: center;
        }
        .sales-person-dashboard-container .no-data-icon-ring {
          width: 60px;
          height: 60px;
          border: 2px solid ${theme.emptyIconRingBorder};
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sales-person-dashboard-container .chart-wrapper {
          padding: 10px 5px;
        }
        .sales-person-dashboard-container .pulse-loader {
          animation: sales-person-dashboard-pulse 1.5s infinite;
          color: #1d8cf8;
          font-weight: bold;
        }
        @keyframes sales-person-dashboard-pulse {
          0% { opacity: 0.4; }
          50% { opacity: 1; }
          100% { opacity: 0.4; }
        }
      `}</style>

      <Row className="d-flex align-items-stretch">
        {periods.map(period => (
          renderSalesPersonCard(period.title, getPeriodData(period.id))
        ))}
      </Row>
    </div>
  );
};

export default SalesPersonDashboard;
